import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faChevronDown, faUsers, faFileCsv, faSearch, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

import AuthContext from '../../contexts/AuthContext';
import Sidebar from '../../components/Sidebar';
import attendanceService from '../../services/attendanceService';
import halaqaService from '../../services/halaqaService';
import '../../styles/admin/AdminAttendanceManagementPage.css';
import '../../styles/DashboardLayout.css';

const AdminAttendanceManagementPage = () => {
  const { user } = useContext(AuthContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Component state
  const [loading, setLoading] = useState(true);
  const [halaqas, setHalaqas] = useState([]);
  const [selectedHalaqa, setSelectedHalaqa] = useState(null); // null means "All Classes"
  const [dateRange, setDateRange] = useState([new Date(new Date().setDate(new Date().getDate() - 30)), new Date()]);
  const [startDate, endDate] = dateRange;
  const [attendanceData, setAttendanceData] = useState([]);
  const [showHalaqaDropdown, setShowHalaqaDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  
  // Fetch halaqas and initialize the page
  useEffect(() => {
    const fetchHalaqas = async () => {
      try {
        setLoading(true);
        const fetchedHalaqas = await halaqaService.getAllHalaqas();
        console.log('Fetched halaqas:', fetchedHalaqas);
        setHalaqas(fetchedHalaqas);
        // Add "All Classes" option
        setSelectedHalaqa(null);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching classes:', error);
        setHalaqas([]); // Set empty array to avoid undefined issues
        setLoading(false);
      }
    };
    
    fetchHalaqas();
  }, []);
  
  // Fetch attendance data when filters change
  useEffect(() => {
    const fetchAttendanceData = async () => {
      if (!startDate || !endDate) return;
      
      try {
        setLoading(true);
        
        // Format dates for API
        const formattedStartDate = format(startDate, 'yyyy-MM-dd');
        const formattedEndDate = format(endDate, 'yyyy-MM-dd');
        
        console.log(`Fetching attendance data from ${formattedStartDate} to ${formattedEndDate}`);
        
        // Sample data for testing during development
        // Will be removed after verifying API integration
        const getSampleAttendanceData = () => {
          const statuses = ['present', 'absent', 'late'];
          const sampleData = [];
          
          // Check if halaqas are available
          const matchingHalaqa = selectedHalaqa || (halaqas.length > 0 ? halaqas[0] : null);
          
          if (!matchingHalaqa) return [];
          
          // Generate some sample records
          for (let i = 0; i < 5; i++) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            
            // Create consistent data format
            const useLegacyFormat = i % 2 === 0; // Use different format for some records for testing
            
            if (useLegacyFormat) {
              sampleData.push({
                _id: `sample-${i}`,
                date: date.toISOString(),
                studentId: {
                  _id: `student-${i}`,
                  name: `Student ${i+1}`
                },
                halaqaId: {
                  _id: matchingHalaqa._id,
                  name: matchingHalaqa.name
                },
                status: statuses[Math.floor(Math.random() * statuses.length)]
              });
            } else {
              // Use flat format (no nested objects) to simulate different API formats
              sampleData.push({
                _id: `sample-${i}`,
                date: date.toISOString(),
                studentName: `Student ${i+1}`,
                studentId: `student-${i}`,  // Just the ID string, not an object
                halaqaName: matchingHalaqa.name,
                halaqaId: matchingHalaqa._id, // Just the ID string, not an object
                status: statuses[Math.floor(Math.random() * statuses.length)]
              });
            }
          }
          
          return sampleData;
        };
        
        // Try to fetch from API first
        let data = [];
        
        try {
          if (selectedHalaqa) {
            // Fetch for specific class
            console.log(`Fetching for specific class: ${selectedHalaqa.name} (${selectedHalaqa._id})`);
            data = await attendanceService.getAttendanceForDateRange(
              selectedHalaqa._id,
              formattedStartDate, 
              formattedEndDate
            );
          } else {
            // Fetch for all classes
            console.log('Fetching for all classes');
            data = await attendanceService.getAllAttendanceForDateRange(
              formattedStartDate,
              formattedEndDate
            );
          }
          
          console.log('Fetched attendance data:', data);
          
          // If no data returned from API, use sample data during development
          if (!data || data.length === 0) {
            // TODO: Remove this in production
            console.warn('No attendance data returned by API, using sample data');
            data = getSampleAttendanceData();
          }
        } catch (apiError) {
          console.error('Error from API, using sample data:', apiError);
          // TODO: Remove this in production
          data = getSampleAttendanceData();
        }
        
        setAttendanceData(Array.isArray(data) ? data : []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching attendance data:', error);
        setAttendanceData([]);
        setLoading(false);
      }
    };
    
    fetchAttendanceData();
  }, [selectedHalaqa, startDate, endDate, halaqas]);
  
  // Toggle halaqa dropdown
  const toggleHalaqaDropdown = () => {
    setShowHalaqaDropdown(!showHalaqaDropdown);
  };
  
  // Handle halaqa selection
  const handleHalaqaSelect = (halaqa) => {
    setSelectedHalaqa(halaqa);
    setShowHalaqaDropdown(false);
  };
  
  // Handle selecting "All Classes"
  const handleSelectAllClasses = () => {
    setSelectedHalaqa(null);
    setShowHalaqaDropdown(false);
  };
  
  // Handle search
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  
  // Handle search on enter key
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // The search is already reactive via the filteredAttendanceData memo,
      // but we can add additional search logic here if needed
      console.log('Searching for:', searchQuery);
    }
  };
  
  // Export attendance data to CSV
  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      
      // Format dates for export
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');
      
      // Call API to get CSV data
      const response = await attendanceService.exportAttendanceToCSV(
        selectedHalaqa ? selectedHalaqa._id : 'all',
        formattedStartDate,
        formattedEndDate
      );
      
      // Create and download the CSV file
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('hidden', '');
      a.setAttribute('href', url);
      a.setAttribute('download', `attendance_${formattedStartDate}_to_${formattedEndDate}.csv`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      setExportLoading(false);
    } catch (error) {
      console.error('Error exporting attendance data:', error);
      alert('Failed to export attendance data');
      setExportLoading(false);
    }
  };
  
  // Apply all filters in a single useMemo to avoid reference errors
  const filteredData = useMemo(() => {
    // Ensure attendanceData is an array
    const dataArray = Array.isArray(attendanceData) ? attendanceData : [];
    let result = dataArray;
    
    // 1. Apply date filtering
    if (startDate && endDate) {
      // Create date objects with time set to beginning/end of day
      const startDateObj = new Date(startDate);
      startDateObj.setHours(0, 0, 0, 0);
      
      const endDateObj = new Date(endDate);
      endDateObj.setHours(23, 59, 59, 999);
      
      console.log(`Filtering by date range: ${startDateObj.toISOString()} to ${endDateObj.toISOString()}`);
      
      result = result.filter(record => {
        if (!record.date) return false;
        
        const recordDate = new Date(record.date);
        return recordDate >= startDateObj && recordDate <= endDateObj;
      });
      
      console.log(`After date filtering: ${result.length} records`);
    }
    
    // 2. Apply search filtering
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      
      result = result.filter(record => {
        // Handle different possible data structures
        let studentName = '';
        if (typeof record.studentId === 'object' && record.studentId) {
          studentName = (record.studentId.name || '').toLowerCase();
        } else if (typeof record.studentName === 'string') {
          studentName = record.studentName.toLowerCase();
        }
        
        let halaqaName = '';
        if (typeof record.halaqaId === 'object' && record.halaqaId) {
          halaqaName = (record.halaqaId.name || '').toLowerCase();
        } else if (typeof record.halaqaName === 'string') {
          halaqaName = record.halaqaName.toLowerCase();
        } else if (typeof record.className === 'string') {
          halaqaName = record.className.toLowerCase();
        }
        
        return studentName.includes(query) || halaqaName.includes(query);
      });
      
      console.log(`After search filtering: ${result.length} records`);
    }
    
    return result;
  }, [attendanceData, searchQuery, startDate, endDate, halaqas]);
  
  // Calculate attendance statistics
  const attendanceStats = useMemo(() => {
    if (!filteredData.length) {
      return { total: 0, present: 0, absent: 0, late: 0 };
    }
    
    const stats = {
      total: filteredData.length,
      present: 0,
      absent: 0,
      late: 0
    };
    
    filteredData.forEach(record => {
      const status = typeof record.status === 'string' ? record.status.toLowerCase() : 'unknown';
      
      if (status === 'present') stats.present++;
      else if (status === 'absent') stats.absent++;
      else if (status === 'late') stats.late++;
    });
    
    return stats;
  }, [filteredData]);
  
  
  // Format date for display helper function
  const formatDisplayDate = (date) => {
    if (!date) return '';
    return format(new Date(date), 'MMM d, yyyy');
  };
  
  if (loading && !attendanceData.length) {
    return (
      <div className="dashboard-layout">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="dashboard-content">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading attendance data...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="dashboard-layout">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="dashboard-content">
        <div className="admin-attendance-page">
          <div className="page-header">
            <div className="header-with-back">
              <Link to="/dashboard" className="back-link">
                <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
              </Link>
              <h1 className="page-title">Attendance Management</h1>
            </div>
          </div>
      
          <div className="filter-section">
            <div className="filter-row">
              {/* Class filter */}
              <div className="filter-group">
                <label className="filter-label">Class</label>
                <div className="dropdown-container">
                  <div 
                    className="dropdown-header" 
                    onClick={toggleHalaqaDropdown}
                  >
                    <FontAwesomeIcon icon={faUsers} className="dropdown-icon" />
                    <span className="dropdown-text">
                      {selectedHalaqa ? selectedHalaqa.name : 'All Classes'}
                    </span>
                    <FontAwesomeIcon icon={faChevronDown} className="dropdown-arrow" />
                  </div>
                  
                  {showHalaqaDropdown && (
                    <div className="dropdown-menu">
                      <div 
                        className={`dropdown-item ${!selectedHalaqa ? 'active' : ''}`}
                        onClick={handleSelectAllClasses}
                      >
                        All Classes
                      </div>
                      {halaqas.map(halaqa => (
                        <div 
                          key={halaqa._id}
                          className={`dropdown-item ${selectedHalaqa && selectedHalaqa._id === halaqa._id ? 'active' : ''}`}
                          onClick={() => handleHalaqaSelect(halaqa)}
                        >
                          {halaqa.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Date range filter */}
              <div className="filter-group">
                <label className="filter-label">Date Range</label>
                <div className="date-range-picker">
                  <FontAwesomeIcon icon={faCalendarAlt} className="date-icon" />
                  <DatePicker
                    selectsRange={true}
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(update) => setDateRange(update)}
                    dateFormat="MMM d, yyyy"
                    maxDate={new Date()}
                    className="date-input"
                  />
                </div>
              </div>
              
              {/* Export button */}
              <div className="filter-group export-group">
                <button 
                  className="export-button"
                  onClick={handleExportCSV}
                  disabled={exportLoading || !filteredData.length}
                >
                  {exportLoading ? (
                    <span>Exporting...</span>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faFileCsv} className="export-icon" />
                      <span>Export CSV</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            
            <div className="filter-row">
              {/* Search */}
              <div className="search-container">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by student or class name..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyPress={handleSearchKeyPress}
                />
              </div>
            </div>
          </div>
          
          {/* Attendance statistics */}
          <div className="attendance-stats">
            <div className="stat-card total-card">
              <div className="stat-value">{attendanceStats.total}</div>
              <div className="stat-label">Total Records</div>
            </div>
            
            <div className="stat-card present-card">
              <div className="stat-value">{attendanceStats.present}</div>
              <div className="stat-label">Present</div>
              <div className="stat-percentage">
                {attendanceStats.total ? Math.round((attendanceStats.present / attendanceStats.total) * 100) : 0}%
              </div>
            </div>
            
            <div className="stat-card absent-card">
              <div className="stat-value">{attendanceStats.absent}</div>
              <div className="stat-label">Absent</div>
              <div className="stat-percentage">
                {attendanceStats.total ? Math.round((attendanceStats.absent / attendanceStats.total) * 100) : 0}%
              </div>
            </div>
            
            <div className="stat-card late-card">
              <div className="stat-value">{attendanceStats.late}</div>
              <div className="stat-label">Late</div>
              <div className="stat-percentage">
                {attendanceStats.total ? Math.round((attendanceStats.late / attendanceStats.total) * 100) : 0}%
              </div>
            </div>
          </div>
          
          {/* Attendance records table */}
          <div className="attendance-table-container">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Student</th>
                  <th>Class</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((record, index) => {
                    // Handle different data structures for student name
                    let studentName = 'Unknown Student';
                    if (typeof record.studentId === 'object' && record.studentId && record.studentId.name) {
                      studentName = record.studentId.name;
                    } else if (record.studentName) {
                      studentName = record.studentName;
                    } else if (typeof record.studentId === 'string' && record.studentId) {
                      // If we only have student ID, try to find the name from other records
                      const studentRecords = filteredData.filter(r => 
                        r.studentId === record.studentId && typeof r.studentName === 'string');
                      if (studentRecords.length > 0) {
                        studentName = studentRecords[0].studentName;
                      }
                    }
                    
                    // Handle different data structures for class/halaqa name
                    let className = 'Unknown Class';
                    if (typeof record.halaqaId === 'object' && record.halaqaId && record.halaqaId.name) {
                      className = record.halaqaId.name;
                    } else if (record.halaqaName) {
                      className = record.halaqaName;
                    } else if (record.className) {
                      className = record.className;
                    } else if (typeof record.halaqaId === 'string') {
                      // Try to find the class name from halaqas state
                      const matchingHalaqa = halaqas.find(h => h._id === record.halaqaId);
                      if (matchingHalaqa) {
                        className = matchingHalaqa.name;
                      } else {
                        // Try to find the class name from other records
                        const classRecords = filteredData.filter(r => 
                          r.halaqaId === record.halaqaId && typeof r.halaqaName === 'string');
                        if (classRecords.length > 0) {
                          className = classRecords[0].halaqaName;
                        }
                      }
                    }
                    
                    // Ensure status is a valid string
                    const status = typeof record.status === 'string' ? record.status : 'unknown';
                    
                    return (
                      <tr key={index}>
                        <td>{formatDisplayDate(record.date)}</td>
                        <td>{studentName}</td>
                        <td>{className}</td>
                        <td>
                          <span className={`status-badge ${status}-status`}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" className="empty-message">
                      {loading ? 'Loading...' : 'No attendance records found for the selected filters'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAttendanceManagementPage;